import React, { useState, useEffect, useCallback } from 'react';
import { Calendar, X, Sparkles } from 'lucide-react';
import { Button } from './ui/button';
import { onboardingAPI } from '../lib/api';

/**
 * OnboardingBanners - Non-blocking, dismissible onboarding nudge system
 * 
 * Two stages:
 * 1. Welcome Banner - Shown immediately after signup
 * 2. Soft Nudge - Shown 7 days after signup if tenant is stuck
 * 
 * Both are permanently dismissible with one click.
 */
export const OnboardingBanners = () => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dismissingWelcome, setDismissingWelcome] = useState(false);
  const [dismissingNudge, setDismissingNudge] = useState(false);

  const fetchStatus = useCallback(async () => {
    try {
      const response = await onboardingAPI.getStatus();
      setStatus(response.data);
    } catch (error) {
      // Silently fail - onboarding is non-critical
      console.error('Failed to fetch onboarding status:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  const handleDismissWelcome = async () => {
    setDismissingWelcome(true);
    try {
      await onboardingAPI.dismissWelcome();
      setStatus(prev => prev ? { ...prev, show_welcome_banner: false } : prev);
    } catch (error) {
      console.error('Failed to dismiss welcome banner:', error);
    } finally {
      setDismissingWelcome(false);
    }
  };

  const handleDismissNudge = async () => {
    setDismissingNudge(true);
    try {
      await onboardingAPI.dismissNudge();
      setStatus(prev => prev ? { ...prev, show_soft_nudge: false } : prev);
    } catch (error) {
      console.error('Failed to dismiss nudge:', error);
    } finally {
      setDismissingNudge(false);
    }
  };

  const handleBookCall = async (source) => {
    // Open Calendly in new tab
    if (status?.calendly_url) {
      window.open(status.calendly_url, '_blank', 'noopener,noreferrer');
    }
    
    // Optimistically mark as booked (prevents future nudges)
    try {
      await onboardingAPI.markBooked();
      setStatus(prev => prev ? { 
        ...prev, 
        show_welcome_banner: false,
        show_soft_nudge: false 
      } : prev);
    } catch (error) {
      // Non-critical, don't block the Calendly flow
      console.error('Failed to mark call as booked:', error);
    }
  };

  // Don't render anything while loading or if no status
  if (loading || !status) {
    return null;
  }

  // Render Stage 1: Welcome Banner (takes priority)
  if (status.show_welcome_banner) {
    return (
      <div 
        className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-4 mb-4"
        data-testid="onboarding-welcome-banner"
      >
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 mt-0.5">
            <Sparkles className="w-5 h-5 text-blue-600" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-sm font-semibold text-blue-900">
              Welcome to Stock & Load 👋
            </h3>
            <p className="mt-1 text-sm text-blue-700">
              Want help getting your vehicles and warehouse set up? We offer free 20-minute onboarding calls.
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              <Button
                size="sm"
                onClick={() => handleBookCall('welcome')}
                className="bg-blue-600 hover:bg-blue-700 text-white"
                data-testid="onboarding-book-call-btn"
              >
                <Calendar className="w-4 h-4 mr-1.5" />
                Book Free Setup Call
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={handleDismissWelcome}
                disabled={dismissingWelcome}
                className="text-blue-700 hover:text-blue-800 hover:bg-blue-100"
                data-testid="onboarding-dismiss-welcome-btn"
              >
                I'll Set It Up Myself
              </Button>
            </div>
          </div>
          <button
            onClick={handleDismissWelcome}
            disabled={dismissingWelcome}
            className="flex-shrink-0 text-blue-400 hover:text-blue-600 transition-colors"
            aria-label="Dismiss welcome banner"
            data-testid="onboarding-close-welcome-btn"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>
    );
  }

  // Render Stage 2: Soft Nudge (7-day)
  if (status.show_soft_nudge) {
    return (
      <div 
        className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4"
        data-testid="onboarding-soft-nudge-banner"
      >
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 mt-0.5">
            <Calendar className="w-5 h-5 text-amber-600" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-sm font-semibold text-amber-900">
              Need help getting set up?
            </h3>
            <p className="mt-1 text-sm text-amber-700">
              We offer free onboarding calls if you'd like a quick walkthrough.
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              <Button
                size="sm"
                onClick={() => handleBookCall('nudge')}
                className="bg-amber-600 hover:bg-amber-700 text-white"
                data-testid="onboarding-nudge-book-btn"
              >
                <Calendar className="w-4 h-4 mr-1.5" />
                Book Free Setup Call
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={handleDismissNudge}
                disabled={dismissingNudge}
                className="text-amber-700 hover:text-amber-800 hover:bg-amber-100"
                data-testid="onboarding-nudge-dismiss-btn"
              >
                No Thanks
              </Button>
            </div>
          </div>
          <button
            onClick={handleDismissNudge}
            disabled={dismissingNudge}
            className="flex-shrink-0 text-amber-400 hover:text-amber-600 transition-colors"
            aria-label="Dismiss nudge banner"
            data-testid="onboarding-close-nudge-btn"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>
    );
  }

  // Nothing to show
  return null;
};

export default OnboardingBanners;
